import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';
import { isOrphan, sentOutreachTaskId } from './outreach-rows';
import type { OutreachPayload } from './outreach-payload';
import type { WebTask } from './tasks';

/**
 * The account's outreaches, read BESIDE the task list instead of inside it.
 *
 * `/api/tasks` used to make this read itself, as a re-entrant hop back out through the public edge, on
 * every single call — and the pill that issues those calls is mounted on every route. It was ~half of
 * that route's median (see `lib/outreach-payload.ts` for the measurement). Splitting it means the tasks
 * land as soon as the task service answers, and the outreach rows are merged into the SAME React Query
 * entry when they arrive, so every reader of that entry — the Tasks screen, the home card, the island
 * quick-view — picks them up with no change of its own.
 *
 * ## What the user sees in the window before the rows arrive
 *
 * A list with its **tasks and no outreach rows**, for as long as the second read takes (bridge p50
 * 160 ms). The rows then appear. Nothing is removed, nothing moves under a click that was already
 * possible, and no row renders in a wrong state — an outreach row simply is not there yet.
 *
 * The one state that WOULD be a lie is an account whose list is entirely outreach rows: it would paint
 * "you have nothing" and then contradict itself. So `fetchTasks` **waits** for this read in exactly that
 * case — an account-wide list that came back with no tasks at all — and only there. Those are the
 * cheapest accounts to wait on, and it is the only window in which the intermediate state is
 * indistinguishable from an empty account.
 *
 * ## A failed read is never "there are none"
 *
 * `/api/outreaches` answers **502**, not an empty list, and this module never caches a payload it did
 * not receive. So a failure leaves the list exactly as it was — showing the rows it already had, or
 * (before the first success, on an empty account) failing the tasks query, which surfaces as the Tasks
 * screen's error state with a retry. What it can never do is claim the user has no messages.
 */

/** The last payload this session read, or `undefined` if none has EVER landed. Distinguishing those two
 *  is the whole point — `undefined` means "not read", never "nothing there". */
export function cachedOutreachPayload(): OutreachPayload | undefined {
  return queryClient.getQueryData<OutreachPayload>(queryKeys.outreaches);
}

export async function fetchOutreachPayload(): Promise<OutreachPayload> {
  const res = await apiFetch(`${API_BASE}/api/outreaches`, { credentials: 'include' });
  if (!res.ok) {
    // Read the server's own reason where there is one; the status alone is the fallback. Either way this
    // THROWS — there is no shape this function returns that means "the read failed".
    const body = (await res.json().catch(() => null)) as { error?: string; message?: string } | null;
    throw new Error(body?.message || body?.error || `outreaches ${res.status}`);
  }
  const data = (await res.json()) as Partial<OutreachPayload>;
  return { received: data.received ?? [], sent: data.sent ?? [] };
}

/**
 * Read the outreaches and merge them into every task list already on screen.
 *
 * Deduped and cached through React Query's own `fetchQuery`, so the several `useTasks`/island readers
 * that fetch in one tick share ONE request — the same collapse `agent-task-invalidation` performs for
 * the task list itself. `staleTime: 0` keeps the freshness exactly what it was when this rode inside
 * `/api/tasks`: re-read whenever the list is.
 */
export async function refreshOutreaches(): Promise<OutreachPayload> {
  const payload = await queryClient.fetchQuery({
    queryKey: queryKeys.outreaches,
    queryFn: fetchOutreachPayload,
    staleTime: 0,
    retry: false,
  });
  // Re-merge into whatever is already cached, so rows appear without waiting for the next task fetch.
  // `mergeOutreaches` is idempotent (it strips the outreach rows before re-deriving them), which is what
  // makes it safe to run over an entry that has already been merged once.
  for (const [key, tasks] of queryClient.getQueriesData<WebTask[]>({ queryKey: queryKeys.tasksAllScopes })) {
    if (!tasks) continue;
    const workspaceId = (key as readonly unknown[])[1];
    queryClient.setQueryData<WebTask[]>(key, mergeOutreaches(tasks, payload, typeof workspaceId === 'string' ? workspaceId : undefined));
  }
  return payload;
}

/**
 * Start the read without waiting for it, and never let its rejection escape as an unhandled one — the
 * caller that DOES want to wait (an empty account-wide list) awaits the returned promise instead.
 *
 * The failure is logged here, once, because the alertable record is the 502 in the access log and
 * `reportError` on the server; this line is only so a developer with a console open is not left
 * wondering why rows are missing.
 */
export function startOutreachRefresh(): Promise<OutreachPayload> {
  const pending = refreshOutreaches();
  pending.catch((err: unknown) => {
    console.error('[outreaches] read failed — the list is showing tasks only, NOT "no messages"', err);
  });
  return pending;
}

/**
 * Fold the outreaches into a task list. Pure, and **idempotent** — running it twice over the same list
 * yields the same list, which is what lets a late payload patch an entry that was already merged.
 *
 * Two rules, and both need the TASK LIST, which is why they live here rather than in the route:
 *
 *  - **Received → rows**, minus the ORPHAN check. An outreach raised BY one of the user's own tasks
 *    already appears as that task; a second row would report one piece of work twice. Uses the shared
 *    `isOrphan`, mirrored 1:1 on iOS and Android.
 *  - **Sent → folded onto the task that raised it**, through the shared `sentOutreachTaskId` (the old
 *    `sourceRecipientTaskId` for rows that still carry it, the worker's `sourceConversationId` for
 *    everything written since #9151).
 *
 * A Space-scoped list gets NO inbound rows — an Outreach has no `workspaceId`, and filing one under a
 * Space it does not belong to would be a lie — but it DOES get the sent fold, which is keyed by task id
 * and entirely workspace-agnostic. Those two were once conflated, and the consequence was that a Space's
 * Tasks list showed no task's sent messages at all.
 */
export function mergeOutreaches(tasks: WebTask[], payload: OutreachPayload, workspaceId?: string): WebTask[] {
  // Idempotence starts here: drop any rows a previous merge added, and any fold it applied, so the
  // payload in hand is the only thing that decides the result.
  const agentTasks = tasks.filter((task) => task.rowKind !== 'outreach');
  const knownTaskIds = new Set(agentTasks.map((t) => t.id).filter((id): id is string => !!id));

  const taskIdByAgentConversation = new Map<string, string>();
  for (const task of agentTasks) {
    if (task.agentConversationId && task.id) taskIdByAgentConversation.set(task.agentConversationId, task.id);
  }
  const sentByTask = new Map<string, WebTask['sentOutreaches']>();
  for (const link of payload.sent) {
    const taskId = sentOutreachTaskId(
      { id: link.summary.id, sourceRecipientTaskId: link.sourceRecipientTaskId, sourceConversationId: link.sourceConversationId },
      taskIdByAgentConversation,
    );
    if (!taskId) continue;
    const list = sentByTask.get(taskId);
    if (list) list.push(link.summary);
    else sentByTask.set(taskId, [link.summary]);
  }

  const withSent = agentTasks.map((task) => {
    const sent = task.id ? sentByTask.get(task.id) : undefined;
    // Re-derived every time rather than patched, so a re-merge cannot leave a stale fold behind.
    if (sent && sent.length > 0) return { ...task, sentOutreaches: sent };
    return task.sentOutreaches ? { ...task, sentOutreaches: undefined } : task;
  });

  if (workspaceId) return withSent;
  const rows = payload.received
    .filter((entry) => isOrphan({ id: entry.row.id ?? '', sourceRecipientTaskId: entry.sourceRecipientTaskId }, knownTaskIds))
    .map((entry) => entry.row);
  return [...withSent, ...rows];
}
